import React, { useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Input from '../../../../components/Form/Input'
import Button from '../../../../components/Form/Button'
import Select from '../../../../components/Form/Select'
import Modal from '../../../../components/Modal'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import { addOneTimePaymentMethodApi, makeOneTimePaymentApi } from '../../../../apis/apis'
import imageBankAccountLight from '../images/bank-account-light.png'
import imageBankAccountDark from '../images/bank-account-dark.png'
import imageBankRoutingLight from '../images/bank-routing-light.png'
import imageBankRoutingDark from '../images/bank-routing-dark.png'
import SecureDataTransfer from './SecureDataTransfer'
import PaymentAmountBalanceWarning from './PaymentAmountBalanceWarning'

interface MakeACHPaymentModalProps {
  isOpen: boolean
  onClose: () => void
  userOptions: Record<string, string>
  defaultUserId: string
  invoiceId: string
  balanceTotal?: number
  balanceDue?: number
}

const MakeACHPaymentModal: React.FC<MakeACHPaymentModalProps> = ({ isOpen, onClose, userOptions, defaultUserId, invoiceId, balanceTotal = 0, balanceDue = 0 }) => {
  const dispatch = useAppDispatch()
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)

  const d = new Date()
  const defaultDate = `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`

  const isValidRoutingNumber = (t = '') => {
    let n = 0
    for (let i = 0; i < t.length; i += 3) {
      n += parseInt(t.charAt(i), 10) * 3 + parseInt(t.charAt(i + 1), 10) * 7 + parseInt(t.charAt(i + 2), 10)
    }

    // If the resulting sum is an even multiple of ten (but not zero),
    // the aba routing number is good.
    return n !== 0 && n % 10 === 0
  }

  const requireStr = 'This field is required'

  // Used to determine if we should use dark mode for the images
  const isDarkMode = document.documentElement.classList.contains('dark')

  if (!isOpen) return null

  return (
    <>
      {confirmModal}
      <CustomContentModal isOpen={isOpen} onClose={onClose}>
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl overflow-hidden w-full max-w-2xl">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Post One Time ACH Payment</h3>
          </div>
          <div className="px-6 py-4">
            <Formik
              initialValues={{
                userId: defaultUserId,
                providerName: '',
                accountHolderName: '',
                accountNumber: '',
                accountNumberConfirm: '',
                routingNumber: '',
                paymentMethodType: 'CHECKING',
                accountHolderType: 'BUSINESS',
                amount: '',
                memo: '',
                paymentDate: defaultDate,
              }}
              validationSchema={Yup.object({
                userId: Yup.string().required(requireStr),
                providerName: Yup.string().trim().required(requireStr),
                accountHolderName: Yup.string().trim().required(requireStr),
                accountNumber: Yup.string()
                  .matches(/^[0-9]+$/, 'Must contain numbers only (no dashes or spaces)')
                  .matches(/^[0-9]{6,17}$/, 'Must be 6 to 17 digits in length')
                  .required(requireStr),
                accountNumberConfirm: Yup.string()
                  .matches(/^[0-9]+$/, 'Must contain numbers only (no dashes or spaces)')
                  .matches(/^[0-9]{6,17}$/, 'Must be 6 to 17 digits in length')
                  .test('max', 'Account numbers must match', function (value) {
                    const { accountNumber } = this.parent
                    return value === accountNumber
                  })
                  .required(requireStr),
                routingNumber: Yup.string()
                  .matches(/^[0-9]+$/, 'Must contain numbers only (no dashes or spaces)')
                  .matches(/^[0-9]{9,}$/, 'Must be 9 or more digits in length')
                  .test('isValidABA', 'The number you entered is not a valid ABA routing number', (s) => isValidRoutingNumber(s))
                  .required(requireStr),
                paymentMethodType: Yup.string().required(requireStr),
                accountHolderType: Yup.string().required(requireStr),
                amount: Yup.string()
                  .required(requireStr)
                  .test('num', 'Must be a number', (value) => !isNaN(Number(value)))
                  .test('min', 'Must be greater than zero', (value) => Number(value) > 0)
                  .matches(/^[0-9]+(.[0-9]{2})?$/, 'Please enter whole dollars and cents (e.g. 100.00)'),
                paymentDate: Yup.string()
                  .required(requireStr)
                  .matches(/^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/\d{4}$/, 'M/D/YYYY format required'),
              })}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  await new Promise((resolve, reject) => {
                    setConfirmModal(<Modal resolve={resolve} reject={reject} />)
                  })
                  setConfirmModal(false)

                  try {
                    setSubmitting(true)

                    // Parse the payment date
                    const [month, day, year] = values.paymentDate.split('/')
                    const paymentDate = new Date(Number(year), Number(month) - 1, Number(day)).getTime()

                    try {
                      // Call oneTimePayment/addPaymentMethod
                      const addPaymentMethodPayload = {
                        userId: values.userId,
                        invoiceId: invoiceId,
                        paymentMethod: {
                          providerName: values.providerName,
                          accountHolderName: values.accountHolderName,
                          accountNumber: values.accountNumber,
                          routingNumber: values.routingNumber,
                          paymentMethodType: values.paymentMethodType,
                          accountHolderType: values.accountHolderType,
                          primary: false,
                          sendEmailNotificationUponCreation: false,
                        },
                      }

                      const addPaymentMethodResponse = await addOneTimePaymentMethodApi(addPaymentMethodPayload)

                      dispatch(
                        pushToast({
                          message: 'Payment method successfully added',
                          type: 'success',
                        })
                      )
                      // Close modal early to prevent downstream errors from the endpoints
                      onClose()
                      setSubmitting(false)

                      try {
                        const makePaymentPayload = {
                          amount: Number(values.amount),
                          paymentDate: paymentDate,
                          memo: values.memo,
                          paymentMethodId: addPaymentMethodResponse.paymentMethodId,
                          invoiceId: invoiceId,
                          userId: values.userId,
                        }

                        await makeOneTimePaymentApi(makePaymentPayload)

                        dispatch(
                          pushToast({
                            message: 'Payment posted',
                            type: 'success',
                          })
                        )
                      } catch (e: any) {
                        // Handle errors from makeOneTimePaymentApi
                        const errorMessage = e.response?.data?.message || e.message || 'Unknown error'

                        dispatch(
                          pushToast({
                            message: `Failed to charge one time payment method: ${errorMessage}`,
                            type: 'error',
                          })
                        )
                        setSubmitting(false)
                        onClose()
                      }
                    } catch (e: any) {
                      // Handle errors from addOneTimePaymentMethodApi
                      const errorMessage = e.response?.data?.message || e.message || 'Unknown error'

                      dispatch(
                        pushToast({
                          message: `Failed to add one-time payment method: ${errorMessage}`,
                          type: 'error',
                        })
                      )
                    }
                  } catch (e) {
                    setConfirmModal(false)
                  }
                } catch (e) {
                  setConfirmModal(false)
                }
              }}
            >
              {({ errors, touched, isSubmitting, submitCount, values }) => (
                <Form>
                  <div className="grid grid-cols-1 gap-4 dark:text-gray-200">
                    <div>
                      <Field
                        component={Select}
                        name="userId"
                        label="On behalf of"
                        options={userOptions}
                        error={errors.userId && touched.userId ? errors.userId : undefined}
                        required
                      />
                    </div>

                    <div>
                      <Field
                        label="Name of Bank"
                        id="providerName"
                        name="providerName"
                        as={Input}
                        required
                        helpText='(e.g. "Bank of America")'
                        error={errors.providerName && touched.providerName ? errors.providerName : undefined}
                      />
                    </div>

                    <div>
                      <Field
                        label="Account Holder Name"
                        id="accountHolderName"
                        name="accountHolderName"
                        as={Input}
                        required
                        helpText='(e.g. "Jones Services LLC" or "Maria Jones")'
                        error={errors.accountHolderName && touched.accountHolderName ? errors.accountHolderName : undefined}
                      />
                    </div>

                    <div>
                      <Field
                        label="Routing Number"
                        id="routingNumber"
                        name="routingNumber"
                        as={Input}
                        required
                        error={errors.routingNumber && touched.routingNumber ? errors.routingNumber : undefined}
                      />
                      <div className="mt-6 mb-6 flex justify-end">
                        <img
                          src={isDarkMode ? imageBankRoutingDark : imageBankRoutingLight}
                          alt="Bank routing number location"
                          className="max-w-[300px] w-full opacity-90"
                        />
                      </div>
                    </div>

                    <div>
                      <Field
                        label="Bank Account Number"
                        id="accountNumber"
                        name="accountNumber"
                        as={Input}
                        required
                        error={errors.accountNumber && touched.accountNumber ? errors.accountNumber : undefined}
                      />
                      <Field
                        label="Bank Account Number (confirm)"
                        id="accountNumberConfirm"
                        name="accountNumberConfirm"
                        as={Input}
                        required
                        className="mt-4"
                        error={errors.accountNumberConfirm && touched.accountNumberConfirm ? errors.accountNumberConfirm : undefined}
                      />
                      <div className="mt-5 mb-5 flex justify-end">
                        <img
                          src={isDarkMode ? imageBankAccountDark : imageBankAccountLight}
                          alt="Bank account number location"
                          className="max-w-[300px] w-full opacity-90"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-left">Account Type</label>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <Field type="radio" name="paymentMethodType" value="CHECKING" id="type-checking" className="mr-2" />
                          <label htmlFor="type-checking">Checking</label>
                        </div>
                        <div className="flex items-center">
                          <Field type="radio" name="paymentMethodType" value="SAVINGS" id="type-savings" className="mr-2" />
                          <label htmlFor="type-savings">Savings</label>
                        </div>
                      </div>
                      {errors.paymentMethodType && touched.paymentMethodType && <div className="text-red-500 text-sm mt-1">{errors.paymentMethodType}</div>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-left">Account Holder Type</label>
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <Field type="radio" name="accountHolderType" value="BUSINESS" id="type-business" className="mr-2" />
                          <label htmlFor="type-business">Business</label>
                        </div>
                        <div className="flex items-center">
                          <Field type="radio" name="accountHolderType" value="PERSONAL" id="type-personal" className="mr-2" />
                          <label htmlFor="type-personal">Personal</label>
                        </div>
                      </div>
                      {errors.accountHolderType && touched.accountHolderType && <div className="text-red-500 text-sm mt-1">{errors.accountHolderType}</div>}
                    </div>

                    <div>
                      <Field
                        label="Amount"
                        id="amount"
                        name="amount"
                        as={Input}
                        required
                        error={errors.amount && touched.amount ? errors.amount : undefined}
                        helpText="Please enter whole dollars and cents (e.g. 100.00)"
                      />
                      <PaymentAmountBalanceWarning amount={values.amount} balanceTotal={balanceTotal} balanceDue={balanceDue} />
                    </div>

                    <div>
                      <Field
                        label="Payment date"
                        id="paymentDate"
                        name="paymentDate"
                        as={Input}
                        required
                        error={errors.paymentDate && touched.paymentDate ? errors.paymentDate : undefined}
                      />
                    </div>

                    <div>
                      <Field label="Memo (optional)" id="memo" name="memo" as={Input} error={errors.memo && touched.memo ? errors.memo : undefined} />
                    </div>

                    {Object.keys(errors).length > 0 && submitCount > 0 && (
                      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                        This form contains one or more errors. Please make corrections and try again.
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between items-center mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <SecureDataTransfer />
                    <div className="space-x-2">
                      <Button type="submit" disabled={isSubmitting}>
                        Post Payment
                      </Button>
                      <Button type="button" onClick={onClose} color="slate">
                        Cancel
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </CustomContentModal>
    </>
  )
}

export default MakeACHPaymentModal
